<template>
	<div class="row centered">
		<AppTable ref="balance-transactions-table"
			v-if="userStore.currBusinessId"
			:fetch-function="fetchFunction"
			:filters="filters"
			:table-headings="visibleTableHeadings"
			:title="getString('balance_transactions')"
			:total-items="cardsStore.cardTransactionsTableTotal || 0"
			:items="cardsStore.cardTransactionsTable || []"
			initial-sort-field="date"
			@failed-fetch="details=>{$emit('failed-fetch', details)}"
			allow-download
			@export="exportTransactions" >

			<template #header-extra-actions>
				<AppButtonIcon
					v-if="hasApifattura"
					prevent-change
					color="var(--color-bg-primary-1)"
					icon-name="icon-send-receipt-blue-new"
					:title="getString('self_invoice_title')"
					@click="isSelfInvoicesOpen = true" />
			</template>

			<template #receipt="{item}">
				<TransactionReceiptButton :item="item" />
			</template>

			<template #self-invoice="{item}">
				<TransactionSelfInvoiceCell :item="item" @retried="refreshTable" />
			</template>

		</AppTable>

		<CardsSelfInvoicesBottomSheet
			v-if="hasApifattura"
			:active="isSelfInvoicesOpen"
			@toggling-popup="closeSelfInvoices"
			@saved="refreshTable" />
	</div>
</template>

<script>
import AppTable from "@/shared/components/AppTable.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import TransactionReceiptButton from "@/console-new/components/config/administration/cards/TransactionReceiptButton.vue";
import TransactionSelfInvoiceCell from "@/console-new/components/config/administration/cards/TransactionSelfInvoiceCell.vue";
import CardsSelfInvoicesBottomSheet from "@/console-new/components/config/administration/cards/CardsSelfInvoicesBottomSheet.vue";
import {useCardsStore, useUserStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";
import {formatDateTimeWithSeconds} from "@/shared/helpers/dates";
import {exportToFile} from "@/shared/helpers/excel";

const RECEIPT_BUCKET_URL = "https://img.qromo.io/";

export default {
	setup() {
		const userStore = useUserStore();
		const cardsStore = useCardsStore();
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		const notificationStore = useNotificationStore();
		return {userStore, cardsStore, getString: languageStore.getString, currencyStore, showNotification: notificationStore.showNotification}
	},
	name: "BalanceTransactionsTable",
	emits: ["failed-fetch"],
	components: {AppTable, AppButtonIcon, TransactionReceiptButton, TransactionSelfInvoiceCell, CardsSelfInvoicesBottomSheet},
	data() {
		return {
			isSelfInvoicesOpen: false,
			filters: [
				{
					type: "date",
					key: 'date',
					tableName: 'cards_authorizations',
					text: this.getString('date'),
					finalValue: '',
					active: false
				},
				{
					type: "price",
					key: "amount",
					text: this.getString('amount'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			tableHeadings: [
				{
					text: this.getString('date'),
					cols: 2,
					sortable: true,
					fieldName: 'date'
				},
				{
					text: this.getString('card'),
					cols: 2,
					fieldName: 'card_name',
				},
				{
					text: this.getString('amount'),
					cols: 2,
					sortable: true,
					fieldName: 'amount',
					formatter: amount => this.currencyStore.readableCurrencyValue(amount, true, true, false)
				},
				{
					text: this.getString('merchant_name'),
					cols: 2,
					fieldName: 'merchant_name'
				},
				{
					text: this.getString('status'),
					cols: 2,
					fieldName: 'display_status',
					formatter: status => this.getDisplayStatus(status)
				},
				{
					text: this.getString('transaction_receipt'),
					cols: 2,
					fieldName: 'receipt_file',
					slotName: 'receipt',
				},
				{
					text: this.getString('self_invoice_short'),
					cols: 2,
					fieldName: 'self_invoice_state',
					slotName: 'self-invoice',
					onlyApifattura: true,
				},
			]
		}
	},
	computed: {
		hasApifattura() {
			return !!this.userStore.currBusiness?.apifattura_id;
		},
		visibleTableHeadings() {
			return this.tableHeadings.filter(heading => !heading.onlyApifattura || this.hasApifattura);
		},
		fetchFunction() {
			// For balance view, always show all cards (cardId = null)
			return (filters, callback) => {
				this.cardsStore.getFilteredCardTransactions(this.userStore.currBusinessId, null, filters, callback);
			};
		}
	},
	methods: {
		closeSelfInvoices(isOpen) {
			if(!isOpen) {
				this.isSelfInvoicesOpen = false;
			}
		},
		refreshTable() {
			if(this.$refs['balance-transactions-table']) {
				this.$refs['balance-transactions-table'].fetchData();
			}
		},
		getSelfInvoiceText(t) {
			if(["sent", "confirmed"].includes(t.self_invoice_state)) {
				return t.self_invoice_numero || "";
			}
			if(t.self_invoice_state === "pending") {
				return this.getString('self_invoice_generating');
			}
			if(t.self_invoice_state) {
				return this.getString(t.self_invoice_state === 'refused' ? 'self_invoice_refused' : 'self_invoice_failed');
			}
			return "";
		},
		getDisplayStatus(status) {
			const statusMap = {
				'pending': this.getString('pending'),
				'processed': this.getString('processed'),
				'refunded': this.getString('refunded')
			};
			return statusMap[status] || status;
		},
		exportTransactions(expExtension) {
			let sym = this.currencyStore.currCurrency.symbol;
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let today = new Date().toLocaleString();
			let fileName = businessNameNorm+"_"+this.getString('balance_transactions').toLowerCase()+"_"+today;
			exportToFile({
				columns: [
					{ header: this.getString('date'), value: t => formatDateTimeWithSeconds(t.date, false) },
					{ header: this.getString('card'), key: 'card_name' },
					{ header: this.getString('amount') + " " + sym, value: t => this.currencyStore.dbToActualValue(t.amount), type: 'number', numberFormat: '#,##0.00' },
					{ header: this.getString('merchant_name'), key: 'merchant_name' },
					{ header: this.getString('status'), value: t => this.getDisplayStatus(t.display_status) },
					{ header: this.getString('transaction_receipt'), value: t => t.receipt_file ? RECEIPT_BUCKET_URL + t.receipt_file : "" },
					...(this.hasApifattura ? [
						{ header: this.getString('self_invoice_short'), value: t => this.getSelfInvoiceText(t) },
						{ header: this.getString('self_invoice_short') + " URL", value: t => t.self_invoice_url || "" },
					] : []),
				],
				rows: this.cardsStore.cardTransactionsTable || [],
				fileName,
				sheetName: this.getString("balance_transactions"),
				format: expExtension,
				onEmpty: () => this.showNotification('table-no-data'),
			});
		},
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== oldVal) {
				if(this.$refs['balance-transactions-table']) {
					this.$refs['balance-transactions-table'].fetchData();
				}
			}
		}
	}
}
</script>

<style scoped lang="scss">

</style>
